/**
 * LinkCallToCandidateDialog — reusable dialog to associate an existing
 * candidate to a "CANDIDAT INCONNU" call log (iter94, CEO 2026-09-01).
 *
 * UX:
 *   - Search box (server-side, /candidates?q=...) with debounce.
 *   - Result list with name / phone / status, click to select.
 *   - Confirm button that fires POST /twilio/calls/{id}/link-candidate.
 */
import * as Dialog from "@radix-ui/react-dialog";
import { Link2, Loader2, Search, User, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";


export default function LinkCallToCandidateDialog({ open, onOpenChange, callLog, onLinked }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [linking, setLinking] = useState(false);

    // Reset on open
    useEffect(() => {
        if (!open) return;
        setQuery(""); setResults([]); setSelectedId(null);
    }, [open]);

    // Debounced search
    useEffect(() => {
        if (!open) return;
        const q = query.trim();
        if (q.length < 2) { setResults([]); return; }
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/candidates", { params: { q, pool: "all" } });
                setResults(Array.isArray(data) ? data.slice(0, 20) : []);
            } catch (_e) {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [query, open]);

    const selected = useMemo(
        () => results.find((c) => c.id === selectedId) || null,
        [results, selectedId],
    );

    const doLink = async () => {
        if (!selectedId || !callLog?.id) return;
        setLinking(true);
        try {
            const { data } = await api.post(`/twilio/calls/${callLog.id}/link-candidate`, {
                candidate_id: selectedId,
            });
            toast.success(`🔗 Appel lié à ${data.candidate_name}`);
            onLinked && onLinked(data);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Liaison impossible");
        } finally {
            setLinking(false);
        }
    };

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-[80] bg-shariti-black/60 backdrop-blur-sm" />
                <Dialog.Content
                    data-testid="link-call-dialog"
                    className="fixed left-1/2 top-1/2 z-[81] w-[92vw] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-white shadow-2xl focus:outline-none max-h-[90vh] overflow-hidden flex flex-col"
                >
                    <div className="p-5 pb-3 border-b border-slate-100">
                        <div className="flex items-center justify-between mb-3">
                            <Dialog.Title className="text-base font-extrabold text-shariti-black inline-flex items-center gap-2">
                                <Link2 className="h-4 w-4 text-shariti-orange" />
                                Associer à un candidat existant
                            </Dialog.Title>
                            <Dialog.Close asChild>
                                <button type="button" className="h-8 w-8 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500">
                                    <X className="h-4 w-4" />
                                </button>
                            </Dialog.Close>
                        </div>
                        <div className="text-[11px] font-bold text-slate-500">
                            Numéro appelé : <span className="font-mono text-shariti-black">{callLog?.to_number || callLog?.from_number || "—"}</span>
                        </div>
                        <div className="relative mt-3">
                            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                placeholder="Nom, prénom, email, téléphone…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                autoFocus
                                data-testid="link-call-search"
                                className="w-full rounded-xl border-2 border-slate-200 pl-10 pr-3 py-2 text-sm font-semibold focus:border-shariti-orange focus:outline-none"
                            />
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3">
                        {loading && (
                            <div className="text-center text-xs font-bold text-slate-400 py-6">
                                <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Recherche…
                            </div>
                        )}
                        {!loading && query.trim().length < 2 && (
                            <div className="text-center text-xs font-bold text-slate-400 py-6">
                                Tapez au moins 2 caractères pour rechercher.
                            </div>
                        )}
                        {!loading && query.trim().length >= 2 && results.length === 0 && (
                            <div className="text-center text-xs font-bold text-slate-400 py-6">
                                Aucun candidat trouvé. Essayez un autre mot-clé.
                            </div>
                        )}
                        <div className="space-y-1.5">
                            {results.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => setSelectedId(c.id)}
                                    data-testid={`link-call-result-${c.id}`}
                                    className={`w-full text-left px-3 py-2 rounded-xl border-2 transition ${
                                        selectedId === c.id
                                            ? "border-shariti-orange bg-shariti-orange-soft/40"
                                            : "border-slate-200 hover:border-shariti-orange/50 bg-white"
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <User className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                                        <span className="font-black text-sm text-shariti-black">
                                            {c.first_name} {c.last_name}
                                        </span>
                                        {c.status && (
                                            <span className="text-[9px] font-bold uppercase tracking-widest text-slate-500 px-1.5 py-0.5 rounded-full bg-slate-100">
                                                {c.status.replace("_", " ")}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-slate-500 font-medium mt-0.5 flex flex-wrap gap-x-2">
                                        {c.phone && <span className="font-mono">{c.phone}</span>}
                                        {c.email && <span>· {c.email}</span>}
                                        {c.layan_offer_title && <span>· {c.layan_offer_title}</span>}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="p-4 border-t border-slate-100 flex gap-2">
                        <button type="button" onClick={() => onOpenChange(false)}
                            className="flex-1 h-11 rounded-xl border-2 border-slate-200 hover:bg-slate-50 font-black text-sm">
                            Annuler
                        </button>
                        <button type="button" onClick={doLink} disabled={!selected || linking}
                            data-testid="link-call-submit"
                            className="flex-1 h-11 rounded-xl bg-shariti-orange hover:bg-shariti-orange-dark text-white font-black text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
                            {linking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                            {linking ? "Liaison…" : (selected ? `Lier à ${selected.first_name}` : "Sélectionner un candidat")}
                        </button>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
